/**
 * ContactDocumentsTab.js
 * Lets users attach documents to a contact via file upload, URL, or screenshot paste.
 * Documents are stored in contact.documents[] and saved via the standard contact PUT.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Link, Clipboard, Trash2, ExternalLink, Loader2, FileText } from 'lucide-react';
import axios from 'axios';
import { uid } from '../../lib/uid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactDocumentsTab({ documents = [], onChange }) {
  const [tab, setTab] = useState('file');
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  // ── Clipboard paste listener (screenshot tab only) ─────────────────────────
  useEffect(() => {
    if (tab !== 'paste') return;
    const handler = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const named = new File(
              [file],
              `Screenshot_${new Date().toISOString().slice(0, 10)}.png`,
              { type: file.type }
            );
            await uploadAndAdd(named);
          }
          break;
        }
      }
    };
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [tab]); // eslint-disable-line

  // ── Upload a File object to GCS, then append to document list ─────────────
  const uploadAndAdd = useCallback(async (file) => {
    setUploading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/files/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const rawPath = res.data?.url || res.data?.view_url || '';
      const uploadedUrl = rawPath.startsWith('/')
        ? `${process.env.REACT_APP_BACKEND_URL}${rawPath}`
        : rawPath;
      onChange([
        ...documents,
        { id: uid(), name: file.name, url: uploadedUrl, added_at: new Date().toISOString() },
      ]);
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  }, [documents, onChange]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await uploadAndAdd(file);
    e.target.value = '';
  };

  const handleAddUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    const name = decodeURIComponent(trimmed.split('/').pop().split('?')[0]) || trimmed;
    onChange([
      ...documents,
      { id: uid(), name, url: trimmed, added_at: new Date().toISOString() },
    ]);
    setUrlInput('');
  };

  const handleDelete = (docId) => onChange(documents.filter(d => d.id !== docId));

  // ── Tab button style ────────────────────────────────────────────────────────
  const tabStyle = (t) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '10px 16px',
    fontSize: 12, fontWeight: 600,
    color: tab === t ? '#1a1a2e' : '#9ca3af',
    borderBottom: tab === t ? '2px solid #c9a84c' : '2px solid transparent',
    background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.15s',
  });

  return (
    <div>
      {/* ── Add Document Panel ─────────────────────────────────────────────── */}
      <div style={{ border: '1px solid #e5e4df', borderRadius: 10, overflow: 'hidden', marginBottom: 24, background: '#fff' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e4df' }}>
          {[
            { key: 'file',  label: 'Upload File', Icon: Upload    },
            { key: 'url',   label: 'Paste URL',   Icon: Link      },
            { key: 'paste', label: 'Screenshot',  Icon: Clipboard },
          ].map(({ key, label, Icon }) => (
            <button key={key} style={tabStyle(key)} onClick={() => setTab(key)}>
              <Icon style={{ width: 13, height: 13 }} />
              {label}
            </button>
          ))}
        </div>

        <div style={{ padding: 16 }}>
          {/* File upload */}
          {tab === 'file' && (
            <>
              <div
                style={{
                  border: '2px dashed #d1d5db', borderRadius: 8, padding: '32px 16px',
                  textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s',
                }}
                onClick={() => fileRef.current?.click()}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#c9a84c'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#d1d5db'}
              >
                {uploading ? (
                  <Loader2 style={{ width: 32, height: 32, margin: '0 auto 8px', color: '#c9a84c', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <>
                    <Upload style={{ width: 32, height: 32, margin: '0 auto 8px', color: '#c9a84c' }} />
                    <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Click to select a file</p>
                    <p style={{ fontSize: 12, color: '#9ca3af' }}>PDF, images, Word docs — any type</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
            </>
          )}

          {/* URL */}
          {tab === 'url' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="url"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddUrl(); }}
                placeholder="https://example.com/document.pdf"
                style={{
                  flex: 1, padding: '8px 12px', fontSize: 13,
                  border: '1px solid #e5e4df', borderRadius: 6, outline: 'none',
                }}
              />
              <button
                onClick={handleAddUrl}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: '#1a1a2e', color: '#c9a84c', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >
                Add
              </button>
            </div>
          )}

          {/* Screenshot / Paste */}
          {tab === 'paste' && (
            <div
              style={{
                border: `2px dashed ${uploading ? '#c9a84c' : '#d1d5db'}`,
                borderRadius: 8, padding: '32px 16px', textAlign: 'center',
              }}
            >
              {uploading ? (
                <Loader2 style={{ width: 32, height: 32, margin: '0 auto 8px', color: '#c9a84c', animation: 'spin 1s linear infinite' }} />
              ) : (
                <>
                  <Clipboard style={{ width: 32, height: 32, margin: '0 auto 8px', color: '#c9a84c' }} />
                  <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>
                    Press{' '}
                    <kbd style={{ padding: '2px 6px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11 }}>
                      Ctrl+V
                    </kbd>{' '}
                    to paste a screenshot
                  </p>
                  <p style={{ fontSize: 12, color: '#9ca3af' }}>
                    Capture with PrintScreen or Snipping Tool first
                  </p>
                </>
              )}
            </div>
          )}

          {error && (
            <p style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: '8px 12px', borderRadius: 6, marginTop: 12 }}>
              {error}
            </p>
          )}
        </div>
      </div>

      {/* ── Document List ──────────────────────────────────────────────────── */}
      {documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <FileText style={{ width: 40, height: 40, margin: '0 auto 12px', color: '#d1d5db' }} />
          <p style={{ fontSize: 14, color: '#9ca3af' }}>No documents yet.</p>
          <p style={{ fontSize: 12, color: '#d1d5db', marginTop: 4 }}>Upload a file, paste a URL, or paste a screenshot above.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map(doc => (
            <div
              key={doc.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 16px', borderRadius: 8,
                background: '#fff', border: '1px solid #e5e4df',
              }}
            >
              <FileText style={{ width: 16, height: 16, flexShrink: 0, color: '#c9a84c' }} />
              <span style={{ flex: 1, fontSize: 14, color: '#1a1a2e', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {doc.name}
              </span>
              <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
                {new Date(doc.added_at).toLocaleDateString()}
              </span>
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ padding: 6, borderRadius: 6, color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                title="Open document"
                onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
              >
                <ExternalLink style={{ width: 15, height: 15 }} />
              </a>
              <button
                onClick={() => handleDelete(doc.id)}
                style={{ padding: 6, borderRadius: 6, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                title="Remove"
                onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
              >
                <Trash2 style={{ width: 15, height: 15 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 16 }}>
        Click <strong>Save</strong> in the top bar to persist document changes.
      </p>
    </div>
  );
}
